import React, { useCallback } from "react";
import { useAgentPresence } from "./AgentPresenceContext";
import { useChatBridge } from "../useChatBridge";
import Whisper from "./Whisper";
import Annotation from "./Annotation";
import Dialog from "./Dialog";
import Toast from "./Toast";

/**
 * Workspace-wide overlay for agent presence elements.
 * Rendered as a fixed overlay in WorkspaceShell.
 *
 * All notifications stack in the same top-right column.
 * Order: whispers → toasts → annotations (newest at bottom).
 * Dialogs render separately as they're interactive modal-like elements.
 * Pulses are inline (rendered by target components, not here).
 */
export default function AgentPresenceLayer(): React.ReactElement | null {
  const { elements, dismissPresence } = useAgentPresence();
  const { sendChatMessage } = useChatBridge();

  const handleReply = useCallback(
    (presenceId: string, text: string) => {
      sendChatMessage(text, { presenceContext: presenceId });
      dismissPresence(presenceId);
    },
    [dismissPresence, sendChatMessage],
  );

  if (elements.length === 0) return null;

  const whispers = elements.filter((e) => e.kind === "whisper");
  const annotations = elements.filter((e) => e.kind === "annotation");
  const dialogs = elements.filter((e) => e.kind === "dialog");
  const toasts = elements.filter((e) => e.kind === "toast");

  const hasNotifications = whispers.length > 0 || toasts.length > 0 || annotations.length > 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 50,
      }}
    >
      {/* All notifications — top right, single stacked column */}
      {hasNotifications && (
        <div
          style={{
            position: "absolute",
            top: 72,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {whispers.map((el) => (
            <Whisper key={el.id} element={el} onDismiss={dismissPresence} />
          ))}
          {toasts.map((el) => (
            <Toast key={el.id} element={el} onDismiss={dismissPresence} onReply={handleReply} />
          ))}
          {annotations.map((el) => (
            <Annotation key={el.id} element={el} onDismiss={dismissPresence} />
          ))}
        </div>
      )}

      {/* Dialogs — centered or bottom-right, interactive */}
      {dialogs.length > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 80,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {dialogs.map((el) => (
            <Dialog key={el.id} element={el} onDismiss={dismissPresence} onReply={handleReply} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes agent-pulse-anim {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
